import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { httpClient } from 'services/api/httpClient';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { parseApiError } from 'utils/errorParser';
import { selectCurrentUser } from 'features/auth/authSelectors';

export const fetchNewspaperOptions = createAsyncThunk(
  'newspaperOptions/fetch',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const user = selectCurrentUser(state);
    
    // If the user's allowed newspapers doesn't include 'ALL', we don't need to fetch the options
    // from the backend because we already know exactly what they can see from their token/profile.
    // This avoids triggering a 403 Forbidden error from the API for restricted users.
    if (user?.allowedNewspapers && !user.allowedNewspapers.includes('ALL')) {
      return user.allowedNewspapers.map(name => ({ id: name, newspaper_name: name }));
    }

    try {
      const { isMockEnabled, mockResponse } = await import('services/api/mockAdapter');
      if (isMockEnabled()) {
        const { MOCK_NEWSPAPERS } = await import('mock/newspaperMaster.mock');
        return await mockResponse(
          MOCK_NEWSPAPERS.map((n) => ({
            id: n.id,
            newspaper_name: n.newspaper_name,
          }))
        );
      }
      return await httpClient.get(API_ENDPOINTS.NEWSPAPERS.OPTIONS);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

const newspaperOptionsSlice = createSlice({
  name: 'newspaperOptions',
  initialState: { items: [], loading: false },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewspaperOptions.pending, (state) => { state.loading = true; })
      .addCase(fetchNewspaperOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchNewspaperOptions.rejected, (state) => { state.loading = false; });
  },
});

export const selectNewspaperOptions = (state) => {
  const options = state.newspaperOptions.items;
  const user = selectCurrentUser(state);
  if (!user) return options;
  const allowed = user.allowedNewspapers || ['ALL'];
  if (allowed.includes('ALL')) return options;
  
  // If the user is restricted, we already know exactly what they can see.
  // We can just construct the options directly from their allowed list,
  // avoiding the need to rely on the /newspapers/options API.
  if (options.length === 0) {
    return allowed.map(name => ({ id: name, newspaper_name: name }));
  }
  return options.filter(opt => allowed.includes(opt.newspaper_name));
};

export const selectNewspaperOptionsLoading = (state) => state.newspaperOptions.loading;

export default newspaperOptionsSlice.reducer;
